import {
  PlaneLanding,
  PlaneTakeoff,
  Building2,
  UserCircle2,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  MapPin,
  Pencil,
  Info,
  CircleCheck,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  SCHEDULE_CONFIDENCE_LABELS,
  SCHEDULE_CONFIDENCE_VARIANTS,
} from '@/types/aircraft-schedules'
import type { ScheduleItem } from './useTodayBoardData'
import type { DetailLevel } from './types'

interface MovementCardProps {
  item: ScheduleItem
  detailLevel: DetailLevel
  compact?: boolean
  onRowClick?: (item: ScheduleItem) => void
  onCheckIn?: (item: ScheduleItem) => void
  onCheckOut?: (item: ScheduleItem) => void
  onRegister?: (item: ScheduleItem) => void
  onShowDetails?: (item: ScheduleItem) => void
}

// ── Provider display ─────────────────────────────────

import { getProviderDisplayName } from '@/lib/utils/provider-display'
import { useIntegrationProviders } from '@/hooks/useIntegrations'
import type { IntegrationProvider } from '@/types/integrations'

function getProviderDisplay(item: ScheduleItem, providers?: IntegrationProvider[] | null): string | null {
  if (item.providerCode) {
    return getProviderDisplayName(item.providerCode, providers)
  }
  if (item.source === 'csr_entered') return 'Staff'
  if (item.source === 'integration') return 'Integration'
  return null
}

// ── Helpers ─────────────────────────────────────────

function getClientName(data: ScheduleItem['data']): string | null {
  if (data.leaseContract?.clientName) return data.leaseContract.clientName
  if (data.transientStay?.clientName) return data.transientStay.clientName
  if (data.aircraft.owner?.companyName) return data.aircraft.owner.companyName
  if (data.aircraft.ownerName) return data.aircraft.ownerName
  return null
}

function getContractLabel(data: ScheduleItem['data']): string | null {
  if (data.leaseContract) return `Lease (${data.leaseContract.status})`
  if (data.transientStay) return `Transient (${data.transientStay.status})`
  return null
}

function getTimeDisplay(item: ScheduleItem): string | null {
  const src = item.scheduledTime || item.sortTime
  if (!src) return null
  const d = new Date(src)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function getAssignedSpace(data: ScheduleItem['data']): string | null {
  const parts: string[] = []
  if (data.allocation.areaName) parts.push(data.allocation.areaName)
  if (data.allocation.unitCode) parts.push(data.allocation.unitCode)
  return parts.length > 0 ? parts.join(' · ') : null
}

function getAirportTag(item: ScheduleItem): string | null {
  if (!item.airportIcao) return null
  return item.movementType === 'arriving'
    ? `from ${item.airportIcao}`
    : `to ${item.airportIcao}`
}

function getCardContext(item: ScheduleItem) {
  const hasAllocation = !!item.allocationId
  const hasContract = !!(item.data.leaseContract || item.data.transientStay)
  const hasSpace = !!(item.data.allocation.unitCode || item.data.allocation.areaName)
  const hasSchedule = !!item.scheduleId
  const isReady = hasAllocation && hasContract
  return { hasAllocation, hasContract, hasSpace, hasSchedule, isReady }
}

function getNotesContent(item: ScheduleItem): string | null {
  const parts: string[] = []
  if (item.flightReference) parts.push(`Ref: ${item.flightReference}`)
  if (item.notes) parts.push(item.notes)
  return parts.length > 0 ? parts.join(' · ') : null
}

// ── Triage + services (Masters demo) ────────────────

const TRIAGE_DOT_CLASS: Record<string, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
}

const SERVICE_LABELS: Record<string, string> = {
  fuel: 'Fuel',
  gpu: 'GPU',
  lav: 'Lav',
  catering: 'Catering',
  rental_car: 'Car',
  hangar_request: 'Hangar',
}

function formatCondition(c?: string | null): string | null {
  return c ? c.replace(/_/g, ' ') : null
}

function ServiceChips({ services }: { services?: Array<Record<string, unknown>> }) {
  if (!services || services.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5">
      {services.map((s, i) => {
        const type = String(s.type ?? '')
        const label = SERVICE_LABELS[type] ?? type
        const gallons = type === 'fuel' && s.gallons ? ` ${s.gallons}g` : ''
        return (
          <Badge key={i} variant="secondary" className="text-[9px] h-4 px-1.5 font-normal">
            {label}{gallons}
          </Badge>
        )
      })}
    </div>
  )
}

// ── Main Component ──────────────────────────────────

export function MovementCard({
  item,
  detailLevel: _detailLevel,
  compact = false,
  onRowClick,
  onCheckIn,
  onCheckOut,
  onRegister,
  onShowDetails,
}: MovementCardProps) {
  const [notesExpanded, setNotesExpanded] = useState(false)
  const { data: providers } = useIntegrationProviders()
  const { data } = item
  const isArrival = item.movementType === 'arriving'
  const isCompleted = item.scheduleStatus === 'completed'
  const clientName = getClientName(data)
  const contractLabel = getContractLabel(data)
  const timeDisplay = getTimeDisplay(item)
  const assignedSpace = getAssignedSpace(data)
  const airportTag = getAirportTag(item)
  const providerDisplay = getProviderDisplay(item, providers)
  const ctx = getCardContext(item)
  const notesContent = getNotesContent(item)

  return (
    <div
      className={`group bg-card rounded-xl border transition-all duration-200 overflow-hidden shadow-xs hover:shadow-md border-border hover:border-border/80 ${
        isCompleted ? 'opacity-60' : ''
      }`}
    >
      <div className={compact ? 'p-3' : 'px-4 py-3'}>
        {/* ── Row 1: Tail + Type + airport | time + direction ── */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {item.triageColor && (
              <span
                className={`h-2.5 w-2.5 rounded-full shrink-0 ${TRIAGE_DOT_CLASS[item.triageColor] ?? ''}`}
                title={`Triage: ${item.triageColor}${item.triageCondition ? ` — ${formatCondition(item.triageCondition)}` : ''}`}
              />
            )}
            <span className={`${compact ? 'text-sm' : 'text-base'} font-bold text-foreground tracking-tight`}>
              {data.aircraft.tailNumber}
            </span>
            {data.aircraft.typeCode && (
              <span className="text-xs text-muted-foreground">{data.aircraft.typeCode}</span>
            )}
            {airportTag && (
              <span className={`text-xs ${isArrival ? 'text-blue-600 dark:text-blue-400' : 'text-violet-600 dark:text-violet-400'}`}>
                {airportTag}
              </span>
            )}
            {item.confidence && item.confidence !== 'confirmed' && (
              <Badge variant={SCHEDULE_CONFIDENCE_VARIANTS[item.confidence]} className="text-[9px] h-4 px-1">
                {SCHEDULE_CONFIDENCE_LABELS[item.confidence]}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {timeDisplay && (
              <span className={`text-sm font-bold font-mono ${
                isArrival ? 'text-blue-600 dark:text-blue-400' : 'text-violet-600 dark:text-violet-400'
              }`}>
                {timeDisplay}
              </span>
            )}
            {isArrival
              ? <PlaneLanding className="h-4 w-4 text-blue-600" />
              : <PlaneTakeoff className="h-4 w-4 text-violet-600" />
            }
          </div>
        </div>

        {/* ── Row 2: Client · Contract · Space — all inline ── */}
        {(clientName || contractLabel || assignedSpace) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mt-1">
            {clientName && (
              <span className="inline-flex items-center gap-1">
                <UserCircle2 className="h-3 w-3 shrink-0" />
                <span className="truncate font-medium">{clientName}</span>
              </span>
            )}
            {contractLabel && (
              <span className="inline-flex items-center gap-1">
                <FileText className="h-3 w-3 shrink-0" />
                <span>{contractLabel}</span>
              </span>
            )}
            {assignedSpace && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="font-medium text-foreground">{assignedSpace}</span>
              </span>
            )}
          </div>
        )}

        {/* ── Service chips (requested services) ── */}
        <ServiceChips services={item.services} />

        {/* ── Notes: first line visible, expand on click ── */}
        {notesContent && (
          <div className="mt-1">
            <button
              type="button"
              className="flex items-start gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors text-left w-full"
              onClick={(e) => { e.stopPropagation(); setNotesExpanded(!notesExpanded) }}
            >
              {notesExpanded
                ? <ChevronUp className="h-3 w-3 shrink-0 mt-0.5" />
                : <ChevronDown className="h-3 w-3 shrink-0 mt-0.5" />
              }
              <span className={notesExpanded ? '' : 'line-clamp-1'}>{notesContent}</span>
            </button>
          </div>
        )}

        {/* ── Bottom bar ── */}
        {isCompleted ? (
          /* Completed: status badge + provider */
          <div className={`flex items-center gap-2 ${compact ? 'mt-2' : 'mt-2.5'} pt-2 border-t`}>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
              <CircleCheck className="h-3 w-3 shrink-0" />
              {isArrival ? 'Landed' : 'Departed'}
            </span>
            {providerDisplay && (
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-muted-foreground/50 border-border/50 font-normal">
                {providerDisplay}
              </Badge>
            )}
          </div>
        ) : (
          /* Active: warnings + provider + action buttons */
          <div className={`flex items-center gap-2 flex-wrap ${compact ? 'mt-2' : 'mt-2.5'} pt-2 border-t`}>
            {/* Left side: warning + provider */}
            <div className="flex items-center gap-2 min-w-0">
              {isArrival && (!ctx.isReady || !ctx.hasSpace) && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                  !ctx.hasAllocation || !ctx.hasContract
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  {!ctx.hasAllocation || !ctx.hasContract ? 'Not registered' : 'No space'}
                </span>
              )}
              {!isArrival && !ctx.hasSchedule && (
                <span className="text-[10px] text-muted-foreground/50 italic">No schedule</span>
              )}
              {item.triageColor && item.triageColor !== 'green' && formatCondition(item.triageCondition) && (
                <span className={`text-[10px] font-medium capitalize ${
                  item.triageColor === 'red'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {formatCondition(item.triageCondition)}
                </span>
              )}
              {providerDisplay && (
                <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-muted-foreground/50 border-border/50 font-normal">
                  {providerDisplay}
                </Badge>
              )}
            </div>

            {/* Right side: action buttons */}
            <div className="flex items-center gap-1.5 ml-auto">
              {/* Details button — full FlightBridge reservation info */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                title="Reservation details"
                onClick={(e) => { e.stopPropagation(); onShowDetails?.(item) }}
              >
                <Info className="h-3.5 w-3.5" />
              </Button>
              {/* Edit schedule button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onRowClick?.(item) }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>

              {isArrival ? (
                <>
                  <Button
                    size="sm"
                    disabled={!ctx.isReady}
                    className={`h-7 px-3 text-[11px] ${ctx.isReady ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onCheckIn?.(item) }}
                  >
                    Check In
                  </Button>
                  {!ctx.isReady && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-3 text-[11px]"
                      onClick={(e) => { e.stopPropagation(); onRegister?.(item) }}
                    >
                      Register
                    </Button>
                  )}
                  {ctx.isReady && !ctx.hasSpace && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-3 text-[11px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      Assign Space
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-[11px]"
                  onClick={(e) => { e.stopPropagation(); onCheckOut?.(item) }}
                >
                  Mark Departed
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
